import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import {
  startServer, stopServer, restartServer, sleepServer, syncServer,
  commandServer, fetchServerStats, fetchServerPlayerStats, fetchServerLogs,
  fetchServerVersions
} from "src/actions/mine_craft/serversActions";
import { useAppDispatch } from "src/store/hooks";
import usePermissions from "src/hooks/usePermissions";

import { Button, Input, message, Select, Switch } from 'antd';
import {
  CaretRightOutlined,
  CopyOutlined,
  DownloadOutlined,
  ExclamationCircleFilled,
  InboxOutlined,
  MoonOutlined,
  PauseOutlined,
  ReloadOutlined,
  SyncOutlined,
  VerticalAlignBottomOutlined
} from '@ant-design/icons';
import Card from "src/componets/Card";
import AlertBlock from "src/componets/AlertBlock";
import UsageCharts from "./_UsageCharts";
import GamerulesForm from "./_GamerulesForm";
import PowerModeForm from "./_PowerModeForm";
import VersionForm from "./_VersionForm";
import {
  BedrockVersions, copyToClipboard, gigabytes, megabytes, playtimeHours, shortFromNow, uptime
} from "./_serverHelpers";

// 500 is crafty-servers' own buffer cap, not an arbitrary ceiling.
const LIMIT_OPTIONS = [100, 200, 500];
const DEFAULT_LIMIT = 200;
// How often the Live toggle re-reads stats, players and the console.
const LIVE_POLL_MS = 5000;
// Distance from the bottom that still counts as following the tail.
const STICK_THRESHOLD_PX = 40;

interface LogLine {
  ts: string;
  line: string;
}

// First severity word wins, so a line merely mentioning "error" further
// along isn't recoloured.
function toneOf(line: string) {
  const match = /\b(ERROR|FATAL|SEVERE|WARN(?:ING)?|INFO|DEBUG)\b/i.exec(line);

  if (!match) return '';

  switch (match[1].toUpperCase()) {
    case 'ERROR':
    case 'FATAL':
    case 'SEVERE':
      return 'o-error';
    case 'WARN':
    case 'WARNING':
      return 'o-warn';
    case 'DEBUG':
      return 'o-debug';
    default:
      return '';
  }
}

/** `2026-08-06T10:00:00Z` → `10:00:00` */
function clockTime(ts: string) {
  const time = new Date(ts);

  if (isNaN(time.getTime())) return ts;

  return time.toLocaleTimeString([], { hour12: false });
}

// % of a configured limit a live value represents — null (not 0) when the
// limit isn't known, so callers can tell "at 0%" apart from "no data to
// compute a percentage from" and fall back to a plain value instead.
function percentOf(used: number | undefined, limit: number | undefined | null) {
  if (used === undefined || !limit) return null;

  return Math.min(100, Math.round(used / limit * 100));
}

// The server console: what state the machine is in and the controls to
// change it, live stats, then the terminal — output on top, command input
// underneath. The Live switch drives one poll for all of it.
const _Server = ({ server }: { server: ServerInterface | undefined }) => {
  const dispatch = useAppDispatch();
  const { write_minecraft } = usePermissions();
  const hosted = server?.hosted_server;

  const [busy, setBusy] = useState(false);
  const [command, setCommand] = useState('');
  const [live, setLive] = useState(false);
  const [gamerulesOpen, setGamerulesOpen] = useState(false);
  const [powerOpen, setPowerOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  const [versions, setVersions] = useState<BedrockVersions>({ stable: [] });

  const [stats, setStats] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [players, setPlayers] = useState<any>(null);
  const [playersLoading, setPlayersLoading] = useState(false);

  const [lines, setLines] = useState<LogLine[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [term, setTerm] = useState('');

  const bodyRef = useRef<HTMLDivElement>(null);
  // Keep pinning the view to the newest line; a scroll up turns it off.
  const stick = useRef(true);

  // Keyed on the id rather than the server object: start/stop/restart/sync
  // all upsert the entity, and depending on its identity would re-trigger
  // the mount effect below on every action.
  const serverId = server?.id;

  const loadStats = useCallback(async (notify = false, silent = false) => {
    if (!serverId) return;

    if (!silent) setStatsLoading(true);
    const resultAction = await dispatch(fetchServerStats(serverId));
    if (!silent) setStatsLoading(false);

    if (fetchServerStats.fulfilled.match(resultAction)) {
      setStats(resultAction.payload);
    } else if (!silent) {
      // Same rule as loadPlayers below — a failed background poll leaves the
      // previous reading in place rather than emptying the tiles.
      setStats(null);
      if (notify) {
        const payload: any = resultAction.payload;
        message.error(Array.isArray(payload) ? payload[0] : 'Stats unavailable');
      }
    }
  }, [serverId, dispatch]);

  const loadPlayers = useCallback(async (notify = false, silent = false) => {
    if (!serverId) return;

    if (!silent) setPlayersLoading(true);
    const resultAction = await dispatch(fetchServerPlayerStats(serverId));
    if (!silent) setPlayersLoading(false);

    if (fetchServerPlayerStats.fulfilled.match(resultAction)) {
      setPlayers(resultAction.payload);
    } else if (!silent) {
      // A background poll that fails keeps the last good reading: blanking
      // the tile to "-" on one flaky response reads as "the data stopped
      // updating", which is worse than a value a few seconds stale.
      setPlayers(null);
      if (notify) {
        const payload: any = resultAction.payload;
        message.error(Array.isArray(payload) ? payload[0] : 'Players unavailable');
      }
    }
  }, [serverId, dispatch]);

  // `silent` is for the Live poll: no spinner, and a failed poll keeps the
  // lines already on screen instead of blanking the console.
  const loadLogs = useCallback(async (silent = false) => {
    if (!serverId) return;

    if (!silent) setLogsLoading(true);
    const resultAction = await dispatch(fetchServerLogs({ id: serverId, limit }));
    if (!silent) setLogsLoading(false);

    if (fetchServerLogs.fulfilled.match(resultAction)) {
      setLines(resultAction.payload?.lines || []);
      setLogsError(null);
      setFetchedAt(new Date().toISOString());
    } else if (!silent) {
      const payload: any = resultAction.payload;
      setLogsError(
        (Array.isArray(payload) ? payload[0] : payload?.errors?.[0]) ||
        resultAction.error?.message ||
        'Console output could not be read'
      );
    }
  }, [serverId, limit, dispatch]);

  const ready = hosted?.state === 'ready';
  const isHosted = !!hosted;
  // A ready server reporting `installing` is being reinstalled on another
  // Bedrock build; its controls wait until the reinstall is done.
  const updating = ready && (hosted?.status || '').toString().toLowerCase() === 'installing';
  const operable = ready && !updating;

  useEffect(() => {
    if (!isHosted) return;

    dispatch(fetchServerVersions())
      .then(action => {
        if (fetchServerVersions.fulfilled.match(action) && Array.isArray(action.payload?.stable)) {
          setVersions({ latest: action.payload.latest, stable: action.payload.stable });
        }
      })
      .catch(console.error);
  }, [isHosted, dispatch]);

  useEffect(() => {
    if (!isHosted) return;

    loadLogs().catch(console.error);

    // Player aggregates read stored history rather than Wings, so they
    // answer even while the server is down — worth showing next to a
    // stopped server, unlike the live Wings readings below.
    loadPlayers().catch(console.error);

    // Stats proxy through to Wings, which only answers once provisioning
    // finished and the container is up.
    if (ready) {
      loadStats().catch(console.error);
    }
  }, [isHosted, ready, loadLogs, loadStats, loadPlayers]);

  useEffect(() => {
    if (!live || !isHosted) return;

    const timer = setInterval(() => {
      loadLogs(true).catch(console.error);
      loadPlayers(false, true).catch(console.error);
      if (ready) loadStats(false, true).catch(console.error);
    }, LIVE_POLL_MS);

    return () => clearInterval(timer);
  }, [live, isHosted, ready, loadLogs, loadStats, loadPlayers]);

  useEffect(() => {
    const body = bodyRef.current;

    if (!body || !stick.current) return;

    body.scrollTop = body.scrollHeight;
  }, [lines]);

  if (!server || !hosted) {
    return (
      <Card>
        <div className='v-srv-empty'>
          <InboxOutlined className='o-icon' />
          <div className='o-title'>Not hosted through crafty-servers</div>
          <div className='o-hint'>Live controls and console are only available for hosted servers.</div>
        </div>
      </Card>
    );
  }

  async function runHostedAction(thunk: any, label: string) {
    if (!server) return;

    setBusy(true);
    const resultAction = await dispatch(thunk(server.id));
    setBusy(false);

    if (thunk.fulfilled.match(resultAction)) {
      message.success(`${label} sent`);
      loadStats().catch(console.error);
      loadPlayers().catch(console.error);
    } else {
      const payload = resultAction.payload;
      const error = Array.isArray(payload) ? payload[0] : resultAction.error?.message;
      message.error(error || `${label} failed`);
    }
  }

  async function sendCommand() {
    if (!server || !command.trim()) return;

    setBusy(true);
    const resultAction = await dispatch(commandServer({ id: server.id, command: command.trim() }));
    setBusy(false);

    if (commandServer.fulfilled.match(resultAction)) {
      message.success('Command sent');
      setCommand('');
      loadLogs(true).catch(console.error);
    } else {
      const payload: any = resultAction.payload;
      message.error(Array.isArray(payload) ? payload[0] : 'Command failed');
    }
  }

  // Provisioning state, live status and the stats "state" all answered
  // "is it up?" separately. One pill answers it now.
  function statusPill() {
    if (hosted?.state === 'failed')     return { tone: 'failed',     label: 'Failed' };
    if (hosted?.state === 'installing') return { tone: 'installing', label: 'Installing' };

    const status = (hosted?.status || '').toString().toLowerCase();

    if (status === 'installing') return { tone: 'installing', label: 'Updating' };

    // Wings only sees the container, which is offline while the server
    // sleeps — crafty's own status is the one that knows the waker still
    // answers on the port.
    if (status === 'sleeping') return { tone: 'sleeping', label: 'Sleeping' };

    const state = (stats?.state || status).toString().toLowerCase();
    const label = state.charAt(0).toUpperCase() + state.slice(1);

    if (state.includes('running'))                    return { tone: 'running',    label: 'Running' };
    if (state === 'starting' || state === 'stopping') return { tone: 'transition', label };
    if (!state)                                       return { tone: 'stopped',    label: 'Unknown' };

    return { tone: 'stopped', label };
  }

  const pill   = statusPill();
  const synced = shortFromNow(hosted.synced_at);
  const outdated = !!server.version && !!versions.latest && server.version !== versions.latest;

  const needle = term.trim().toLowerCase();
  const visible = needle
    ? lines.filter(entry => entry.line?.toLowerCase().includes(needle))
    : lines;

  function onScroll() {
    const body = bodyRef.current;

    if (!body) return;

    stick.current = body.scrollHeight - body.scrollTop - body.clientHeight <= STICK_THRESHOLD_PX;
  }

  function scrollToBottom() {
    const body = bodyRef.current;

    if (!body) return;

    stick.current = true;
    body.scrollTop = body.scrollHeight;
  }

  const asText = (list: LogLine[]) =>
    list.map(entry => `[${entry.ts}] ${entry.line}`).join('\n');

  function copyAll() {
    if (!visible.length) return;

    copyToClipboard(asText(visible), `${visible.length} lines copied`);
  }

  function download() {
    if (!visible.length || !server) return;

    const blob = new Blob([asText(visible)], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `server-${server.id}-console.log`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    message.success('Console log downloaded');
  }

  const fetched = shortFromNow(fetchedAt);

  // The lifetime aggregates that ride along with the live count — too much
  // for a tile that has to stay one line, but exactly what you want when
  // asking "is 3 online a lot for this server?".
  const playerHint = players
    ? [
        players.uniquePlayers !== undefined && `${players.uniquePlayers} unique players`,
        players.totalSessions !== undefined && `${players.totalSessions} sessions`,
        players.totalPlaytimeSeconds !== undefined && `${playtimeHours(players.totalPlaytimeSeconds)} total playtime`
      ].filter(Boolean).join(' · ')
    : undefined;

  const StatTile = ({ label, value, hint }: { label: string, value: React.ReactNode, hint?: string }) => (
    <div className='v-srv-stat' title={hint}>
      <div className='o-label'>{label}</div>
      <div className='o-value'>{value}</div>
    </div>
  );

  // Ring + used/total for any live value measured against a configured
  // limit — CPU/memory/disk quotas and the player cap all share this shape.
  // `percent` is null when the limit isn't known (see
  // HostedServerInterface#limits): falls back to the plain value-only tile
  // rather than fabricate a percentage.
  const UsageTile = ({ label, valueText, percent, hint }: { label: string, valueText: React.ReactNode, percent: number | null, hint?: string }) => {
    if (percent === null) {
      return <StatTile label={label} value={valueText} hint={hint} />;
    }

    const tone = percent >= 95 ? 'o-danger' : percent >= 80 ? 'o-warning' : '';

    return (
      <div className={`v-srv-stat v-srv-stat--ring ${tone}`} title={hint}>
        <svg className='o-ring' viewBox='0 0 36 36'>
          <g transform='rotate(-90 18 18)'>
            <circle className='o-ring-bg' cx='18' cy='18' r='15.5' />
            <circle
              className='o-ring-fg'
              cx='18' cy='18' r='15.5'
              pathLength={100}
              strokeDasharray={`${percent} ${100 - percent}`}
            />
          </g>
          <text className='o-ring-text' x='18' y='18' textAnchor='middle' dominantBaseline='central'>{percent}%</text>
        </svg>
        <div>
          <div className='o-label'>{label}</div>
          <div className='o-value'>{valueText}</div>
        </div>
      </div>
    );
  };

  return (
    <>
      {(hosted.state === 'failed' || hosted.error) &&
        <div className='v-srv-banner'>
          <ExclamationCircleFilled className='o-icon' />
          <div className='o-text'>
            <b>{hosted.state === 'failed' ? 'Provisioning failed.' : 'Last sync reported an error.'}</b>
            {hosted.error ? ` ${hosted.error}` : ' crafty-servers did not finish creating this server.'}
          </div>
        </div>
      }

      <Card className='v-srv-control'>
        <div className='o-status'>
          <span className={`v-srv-status o-${pill.tone}`}>
            <span className='o-dot' />{pill.label}
          </span>
          <div className='o-sub'>
            {hosted.state}{synced && ` · synced ${synced}`}
            {stats?.uptimeMs !== undefined && ` · uptime ${uptime(stats.uptimeMs)}`}
            {server.version && ` · Bedrock ${server.version}`}
            {outdated && !updating &&
              <span className='o-update' title='Players on the newer build cannot join this server'>
                {` · ${versions.latest} available`}
              </span>
            }
          </div>
        </div>

        <div className='o-actions'>
          {write_minecraft &&
            <>
              <Button
                type='primary'
                icon={<CaretRightOutlined />}
                disabled={busy || !operable}
                onClick={() => runHostedAction(startServer, 'Start')}
              >
                Start
              </Button>
              <Button
                icon={<PauseOutlined />}
                disabled={busy || !operable}
                onClick={() => runHostedAction(stopServer, 'Stop')}
              >
                Stop
              </Button>
              <Button
                icon={<MoonOutlined />}
                disabled={busy || !operable || pill.tone === 'sleeping'}
                title='Stop, but keep the port answering — the server starts again when a player joins'
                onClick={() => runHostedAction(sleepServer, 'Sleep')}
              >
                Sleep
              </Button>
              <Button
                icon={<ReloadOutlined />}
                disabled={busy || !operable}
                onClick={() => runHostedAction(restartServer, 'Restart')}
              >
                Restart
              </Button>
              <Button
                disabled={busy || !operable}
                onClick={() => setGamerulesOpen(true)}
              >
                Gamerules
              </Button>
              <Button
                disabled={busy || !operable}
                title='Sleep when idle or always on'
                onClick={() => setPowerOpen(true)}
              >
                Power
              </Button>
              <Button
                className={outdated && !updating ? 'o-outdated' : undefined}
                disabled={busy || !operable}
                title={outdated ? `Bedrock ${versions.latest} is out — update so players on it can join` : 'Change the Bedrock build'}
                onClick={() => setVersionOpen(true)}
              >
                Version
              </Button>
              <Button
                className='o-icon-btn'
                icon={<SyncOutlined />}
                title='Refresh'
                aria-label='Refresh'
                disabled={busy}
                onClick={() => runHostedAction(syncServer, 'Refresh')}
              />
            </>
          }

          {/* Auto-refresh sits with the manual Refresh it automates. Reading
              only, so read-only admins get it too. */}
          <label className='o-live' title={`Auto-refresh stats, players and the console every ${LIVE_POLL_MS / 1000}s`}>
            <Switch size='small' checked={live} onChange={setLive} />
            <span className='o-live-label'>Live</span>
          </label>
        </div>

        <div className='o-stats'>
          {(statsLoading && !stats) || (playersLoading && !players)
            ? [0, 1, 2, 3].map(index => <div key={index} className='v-srv-stat o-skeleton' />)
            : <>
                <UsageTile
                  label='CPU'
                  percent={percentOf(stats?.cpuPercent, hosted.limits?.cpu_percent)}
                  valueText={stats?.cpuPercent === undefined
                    ? '-'
                    : `${stats.cpuPercent?.toFixed ? stats.cpuPercent.toFixed(1) : stats.cpuPercent}%`
                      + (hosted.limits?.cpu_percent ? ` / ${hosted.limits.cpu_percent}%` : '')}
                />
                <UsageTile
                  label='Memory'
                  percent={percentOf(stats?.memoryBytes, hosted.limits?.memory_mb && hosted.limits.memory_mb * 1024 * 1024)}
                  valueText={hosted.limits?.memory_mb
                    ? `${megabytes(stats?.memoryBytes)} / ${megabytes(hosted.limits.memory_mb * 1024 * 1024)}`
                    : megabytes(stats?.memoryBytes)}
                />
                <UsageTile
                  label='Disk Usage'
                  percent={percentOf(stats?.diskBytes, hosted.limits?.disk_mb && hosted.limits.disk_mb * 1024 * 1024)}
                  valueText={hosted.limits?.disk_mb
                    ? `${gigabytes(stats?.diskBytes)} / ${gigabytes(hosted.limits.disk_mb * 1024 * 1024)}`
                    : gigabytes(stats?.diskBytes)}
                />
                <UsageTile
                  label='Players Online'
                  percent={percentOf(players?.currentOnline, players?.maxPlayers)}
                  valueText={players?.currentOnline === undefined
                    ? '-'
                    : (players?.maxPlayers != null
                        ? `${players.currentOnline}/${players.maxPlayers}`
                        : String(players.currentOnline))}
                  hint={playerHint}
                />
              </>
          }
        </div>
      </Card>

      <UsageCharts server={server} live={live} />

      <Card className='v-srv-logs'>
        <div className='o-head'>
          <h4 className='v-content-title'>Console</h4>

          <div className='o-meta'>
            {visible.length}{needle && ` of ${lines.length}`} line{visible.length === 1 ? '' : 's'}
            {fetched && ` · read ${fetched}`}
          </div>

          <div className='o-tools'>
            <Input.Search
              className='o-filter'
              placeholder='Filter lines'
              allowClear
              value={term}
              onChange={event => setTerm(event.target.value)}
            />

            <Select
              className='o-limit'
              value={limit}
              onChange={setLimit}
              options={LIMIT_OPTIONS.map(value => ({ value, label: `${value} lines` }))}
            />

            <Button
              className='o-icon-btn'
              icon={<SyncOutlined />}
              title='Refresh'
              aria-label='Refresh console output'
              loading={logsLoading}
              onClick={() => loadLogs()}
            />
            <Button
              className='o-icon-btn'
              icon={<CopyOutlined />}
              title='Copy'
              aria-label='Copy console output'
              disabled={!visible.length}
              onClick={copyAll}
            />
            <Button
              className='o-icon-btn'
              icon={<DownloadOutlined />}
              title='Download'
              aria-label='Download console output'
              disabled={!visible.length}
              onClick={download}
            />
          </div>
        </div>

        {logsError &&
          <AlertBlock type='error' message={logsError} onClose={() => setLogsError(null)} />
        }

        <div className='o-terminal'>
          <div className='o-body' ref={bodyRef} onScroll={onScroll}>
            {logsLoading && !lines.length &&
              [0, 1, 2, 3, 4].map(index => <div key={index} className='o-line o-skeleton' />)
            }

            {visible.map((entry, index) => (
              <div key={index} className={`o-line ${toneOf(entry.line || '')}`}>
                <span className='o-ts'>{clockTime(entry.ts)}</span>
                <span className='o-text'>{entry.line}</span>
              </div>
            ))}

            {!logsLoading && !logsError && !visible.length &&
              <div className='o-none'>
                {lines.length
                  ? `No line matches “${term.trim()}”.`
                  : hosted.state === 'ready'
                    ? 'The console buffer is empty — crafty-servers keeps it in memory, so it starts over whenever their API restarts.'
                    : 'The server has to finish provisioning before it writes to the console.'}
              </div>
            }
          </div>

          {write_minecraft &&
            <div className={`o-cmd ${!operable ? 'o-disabled' : ''}`}>
              <span className='o-prompt'>❯</span>
              <Input
                className='o-input'
                bordered={false}
                placeholder='say Hello world'
                value={command}
                maxLength={512}
                disabled={busy || !operable}
                onChange={e => setCommand(e.target.value)}
                onPressEnter={sendCommand}
              />
              <Button
                type='primary'
                className='o-send'
                disabled={busy || !operable || !command.trim()}
                onClick={sendCommand}
              >
                Send
              </Button>
            </div>
          }

          <div className='o-foot'>
            <span className='o-hint'>
              Last {limit} lines buffered by crafty-servers · not a full history
            </span>

            <Button
              size='small'
              type='text'
              className='o-jump'
              icon={<VerticalAlignBottomOutlined />}
              onClick={scrollToBottom}
            >
              Jump to latest
            </Button>
          </div>
        </div>
      </Card>

      <GamerulesForm server={server} open={gamerulesOpen} onClose={() => setGamerulesOpen(false)} />
      <PowerModeForm server={server} open={powerOpen} onClose={() => setPowerOpen(false)} />
      <VersionForm server={server} versions={versions} open={versionOpen} onClose={() => setVersionOpen(false)} />
    </>
  );
}

export default _Server;
